import React from "react";
import CodeMirror from "@uiw/react-codemirror";
import { Play, Trash2, Sun, Moon, Shield } from "lucide-react";
import { Spinner } from "react-bootstrap";
import { oneDark, defaultLightThemeOption } from "@uiw/react-codemirror";
import { Accordion, AccordionSummary, AccordionDetails, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Tooltip from "@mui/material/Tooltip";
import ProctoringWarningModal from "../proctoring/ProctoringWarningModal";
import ReadyForCodingAssignment from "./ReadyForCodingAssignment";

const ProctoredCodeEditorDB = ({
    isDarkMode,
    proctoringContainerRef,
    // Start screen
    showProctoringStart,
    handleStartProctoredSession,
    // Submit confirm
    showSubmitConfirm,
    setShowSubmitConfirm,
    handleSubmitCode,
    // Violation modal
    activeViolation,
    totalViolations,
    handleResumeAfterViolation,
    // Submission state
    isSubmitted,
    showCompilerAfterSubmit,
    setShowCompilerAfterSubmit,
    // Timer
    timeLeft,
    formatTime,
    // Proctoring data for results view
    proctoringLogs,
    pasteEvents,
    // Content & language
    activeContent,
    activeLanguage,
    problemStatement,
    // Editor state
    activeFile,
    loading,
    handleRunCode,
    handleClearCode,
    toggleTheme,
    extensions,
    output,
    error,
    // SQL-specific
    tables,
    renderTables,
    onChange,
}) => {

    // ── Styles ──────────────────────────────────────────────────────────────────
    const bg = isDarkMode ? "#0f0f23" : "#f8fafc";
    const surface = isDarkMode ? "#1e1e2e" : "#ffffff";
    const border = isDarkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.08)";
    const textPrimary = isDarkMode ? "#e2e8f0" : "#374151";
    const textSecondary = isDarkMode ? "#9ca3af" : "#6b7280";

    const toolbarStyles = {
        display: "flex",
        alignItems: "center",
        gap: "10px",
        padding: "8px 16px",
        height: "60px",
        background: isDarkMode ? "linear-gradient(90deg,#252544,#252544)" : "linear-gradient(90deg,#e0e7ff,#c3daff)",
        borderBottom: isDarkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.08)",
        flexShrink: 0,
        boxSizing: "border-box",
    };

    const btnBase = {
        border: "none",
        borderRadius: "10px",
        width: "46px",
        height: "46px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        flexShrink: 0,
        boxShadow: "0 3px 6px rgba(0,0,0,0.2)",
    };

    // ── Start screen ─────────────────────────────────────────────────────────
    if (showProctoringStart) {
        return (
            <div style={{
                position: "absolute", inset: 0, zIndex: 200,
                background: "#f1f5f9",
                display: "flex", alignItems: "center", justifyContent: "center",
                padding: "2rem", borderRadius: "8px", overflowY: "auto",
            }}>
                <ReadyForCodingAssignment
                    startHandler={handleStartProctoredSession}
                    language="MySQL"
                    testCasesCount={activeContent?.testCases?.length || 0}
                    timerMinutes={activeContent?.timerMinutes}
                    isProctored={true}
                    contentType="sql"
                    title="Ready For SQL Assignment"
                    description="Write your SQL query, run it against the test schema, and submit your best solution!"
                />
            </div>
        );
    }

    // ── Submit confirm overlay ────────────────────────────────────────────────
    const submitConfirmOverlay = showSubmitConfirm && (
        <div style={{
            position: "fixed", inset: 0, zIndex: 9999,
            background: "rgba(0,0,0,0.75)",
            display: "flex", alignItems: "center", justifyContent: "center",
        }}>
            <div style={{
                background: isDarkMode ? "#1f2937" : "#ffffff",
                borderRadius: "16px", padding: "2rem 2.5rem",
                maxWidth: "430px", width: "90%", textAlign: "center",
                border: isDarkMode ? "1px solid #4a5568" : "1px solid #e5e7eb",
                boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
            }}>
                <div style={{ fontSize: "2.5rem", marginBottom: "0.75rem" }}>⚠️</div>
                <h3 style={{ color: isDarkMode ? "#f9fafb" : "#111827", fontWeight: 700, marginBottom: "0.75rem", fontSize: "1.2rem" }}>
                    Submit Your Solution?
                </h3>
                <p style={{ color: textSecondary, lineHeight: 1.65, marginBottom: "1.75rem", fontSize: "0.95rem" }}>
                    This will <strong>permanently submit</strong> your SQL query and end the proctored session.
                    Your proctoring report and all code snapshots will be saved.
                </p>
                <div style={{ display: "flex", gap: "1rem", justifyContent: "center" }}>
                    <button
                        onClick={() => setShowSubmitConfirm(false)}
                        style={{
                            background: "transparent",
                            border: isDarkMode ? "1px solid #4a5568" : "1px solid #d1d5db",
                            color: isDarkMode ? "#d1d5db" : "#374151",
                            borderRadius: "10px", padding: "0.6rem 1.5rem",
                            fontWeight: 600, cursor: "pointer", fontSize: "0.95rem",
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={() => { setShowSubmitConfirm(false); handleSubmitCode("manual"); }}
                        style={{
                            background: "linear-gradient(135deg,#10b981,#059669)",
                            border: "none", color: "#fff",
                            borderRadius: "10px", padding: "0.6rem 1.75rem",
                            fontWeight: 700, cursor: "pointer", fontSize: "0.95rem",
                            boxShadow: "0 4px 12px rgba(16,185,129,0.4)",
                        }}
                    >
                        Yes, Submit
                    </button>
                </div>
            </div>
        </div>
    );

    // ── Violation modal ───────────────────────────────────────────────────────
    const violationModal = !isSubmitted && (
        <ProctoringWarningModal
            show={!!activeViolation}
            violationType={activeViolation}
            violationCount={totalViolations}
            onResume={handleResumeAfterViolation}
            container={proctoringContainerRef}
        />
    );

    // ── Results view (post-submission) ────────────────────────────────────────
    if (isSubmitted && !showCompilerAfterSubmit) {
        const submittedCode = activeContent?.codeSnippet || activeFile?.content || "";
        const submittedAt = activeContent?.submittedAt?.$date || activeContent?.submittedAt;
        const submitReason = activeContent?.submitReason || "manual";
        const pd = activeContent?.codeProctoringData;
        const violations = pd?.violations || proctoringLogs || [];
        const pasteCount = pd?.pasteEvents?.length ?? pasteEvents.length;

        return (
            <>
                {submitConfirmOverlay}
                {violationModal}
                <div style={{ height: "100%", overflowY: "auto", display: "flex", flexDirection: "column", background: isDarkMode ? "#1a202c" : "#f0f2f5" }}>
                    {/* Banner */}
                    <div style={{ background: "linear-gradient(135deg,#10b981,#059669)", color: "#fff", padding: "1.25rem 2rem", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
                        <div>
                            <h3 style={{ margin: 0, fontWeight: 700, fontSize: "1.2rem" }}>✅ SQL Query Submitted</h3>
                            <div style={{ fontSize: "0.85rem", opacity: 0.9, marginTop: "4px" }}>
                                {submitReason === "timer" ? "Auto-submitted (time expired)" : "Manually submitted"}
                                {submittedAt && ` · ${new Date(submittedAt).toLocaleString()}`}
                            </div>
                        </div>
                        <button
                            onClick={() => setShowCompilerAfterSubmit(true)}
                            style={{ background: "#fff", color: "#059669", border: "none", borderRadius: "10px", padding: "0.55rem 1.4rem", fontWeight: 700, fontSize: "0.9rem", cursor: "pointer" }}
                        >
                            🔄 Run in Compiler
                        </button>
                    </div>

                    {/* Body */}
                    <div style={{ display: "flex", flex: 1, gap: "1.25rem", padding: "1.25rem", overflow: "hidden", minHeight: 0 }}>
                        {/* Left: submitted SQL (read-only) */}
                        <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
                            <h4 style={{ color: isDarkMode ? "#93c5fd" : "#1e40af", fontWeight: 700, marginBottom: "0.6rem", fontSize: "0.95rem" }}>
                                Submitted SQL Query
                            </h4>
                            <div style={{ flex: 1, borderRadius: "8px", overflow: "hidden", border: isDarkMode ? "1px solid #4a5568" : "1px solid #e5e7eb" }}>
                                <CodeMirror
                                    value={submittedCode}
                                    height="100%"
                                    extensions={extensions}
                                    theme={isDarkMode ? oneDark : defaultLightThemeOption}
                                    readOnly
                                    style={{ height: "100%" }}
                                />
                            </div>
                        </div>

                        {/* Right: details + teacher review */}
                        <div style={{ width: "270px", flexShrink: 0, display: "flex", flexDirection: "column", gap: "1rem" }}>
                            {/* Details */}
                            <div style={{ background: isDarkMode ? "#1f2937" : "#fff", borderRadius: "12px", padding: "1.1rem", border: isDarkMode ? "1px solid #4a5568" : "1px solid #e5e7eb" }}>
                                <h4 style={{ color: isDarkMode ? "#f9fafb" : "#111827", fontWeight: 700, marginBottom: "0.5rem", fontSize: "0.95rem" }}>📝 Details</h4>
                                <div style={{ color: textSecondary, fontSize: "0.84rem", lineHeight: 1.8 }}>
                                    <div><strong>Language:</strong> SQL</div>
                                    <div><strong>Submit:</strong> {submitReason === "timer" ? "Timer expired" : "Manual"}</div>
                                    {submittedAt && <div><strong>Time:</strong> {new Date(submittedAt).toLocaleTimeString()}</div>}
                                </div>
                            </div>

                            {/* Teacher review — score */}
                            {activeContent?.teacher_reviewed ? (() => {
                                const earned = activeContent.earnedMarks ?? 0;
                                const total = activeContent.totalMarks ?? (activeContent.marksPerTestCase * (activeContent.testCases?.length || 0));
                                const pct = total > 0 ? Math.round((earned / total) * 100) : 0;
                                const scoreColor = pct >= 80 ? "#10b981" : pct >= 50 ? "#f59e0b" : "#ef4444";
                                return (
                                    <div style={{ background: isDarkMode ? "#1f2937" : "#fff", borderRadius: "12px", padding: "1.1rem", border: `1px solid ${scoreColor}40` }}>
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.75rem" }}>
                                            <h4 style={{ color: isDarkMode ? "#f9fafb" : "#111827", fontWeight: 700, margin: 0, fontSize: "0.95rem" }}>⭐ Your Score</h4>
                                            <span style={{ background: "#d1fae5", color: "#065f46", fontSize: "0.68rem", fontWeight: 700, padding: "2px 8px", borderRadius: "20px" }}>REVIEWED</span>
                                        </div>
                                        <div style={{ textAlign: "center", padding: "0.25rem 0 0.5rem" }}>
                                            <div style={{ fontSize: "2.8rem", fontWeight: 800, color: scoreColor, lineHeight: 1 }}>{earned}</div>
                                            <div style={{ color: isDarkMode ? "#9ca3af" : "#6b7280", fontSize: "0.82rem", marginTop: "2px" }}>out of {total} marks</div>
                                        </div>
                                        <div style={{ background: isDarkMode ? "#374151" : "#f3f4f6", borderRadius: "8px", height: "8px", overflow: "hidden", margin: "0.5rem 0" }}>
                                            <div style={{ width: `${pct}%`, height: "100%", background: scoreColor, borderRadius: "8px", transition: "width 0.6s ease" }} />
                                        </div>
                                        <div style={{ textAlign: "center", fontSize: "0.78rem", color: scoreColor, fontWeight: 700 }}>{pct}%</div>
                                    </div>
                                );
                            })() : (
                                <div style={{ background: isDarkMode ? "#1f2937" : "#fffbeb", borderRadius: "12px", padding: "1rem", border: "1px solid #fcd34d", textAlign: "center" }}>
                                    <div style={{ fontSize: "1.4rem", marginBottom: "4px" }}>⏳</div>
                                    <div style={{ fontWeight: 700, fontSize: "0.85rem", color: isDarkMode ? "#fcd34d" : "#92400e" }}>Pending Review</div>
                                    <div style={{ fontSize: "0.75rem", color: isDarkMode ? "#9ca3af" : "#6b7280", marginTop: "4px" }}>Your instructor will review and assign marks</div>
                                </div>
                            )}

                            {/* Teacher feedback */}
                            {activeContent?.teacher_reviewed && activeContent?.teacherFeedback && (
                                <div style={{ background: isDarkMode ? "#1f2937" : "#eff6ff", borderRadius: "12px", padding: "1.1rem", border: "1px solid #bfdbfe" }}>
                                    <h4 style={{ color: isDarkMode ? "#93c5fd" : "#1e40af", fontWeight: 700, marginBottom: "0.5rem", fontSize: "0.95rem" }}>💬 Teacher Feedback</h4>
                                    <p style={{ color: isDarkMode ? "#d1d5db" : "#374151", fontSize: "0.85rem", lineHeight: 1.65, margin: 0, whiteSpace: "pre-wrap" }}>
                                        {activeContent.teacherFeedback}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </>
        );
    }

    // ── LeetCode layout (live session + compiler-after-submit) ─────────────────
    return (
        <>
            {submitConfirmOverlay}
            {violationModal}
            <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>

                {/* Toolbar */}
                <div style={toolbarStyles}>
                    {/* Proctoring badge — live session only */}
                    {!showCompilerAfterSubmit && (
                        <div style={{
                            display: "flex", alignItems: "center", gap: "6px",
                            background: totalViolations > 0 ? "rgba(245,158,11,0.15)" : "rgba(16,185,129,0.15)",
                            border: `1px solid ${totalViolations > 0 ? "#f59e0b" : "#10b981"}`,
                            borderRadius: "8px", padding: "4px 10px",
                            color: totalViolations > 0 ? "#f59e0b" : "#10b981",
                            fontSize: "0.78rem", fontWeight: 700, flexShrink: 0,
                        }}>
                            <Shield size={14} />
                            {totalViolations > 0 ? `Warnings: ${totalViolations}` : "Proctored"}
                        </div>
                    )}
                    {showCompilerAfterSubmit && (
                        <button
                            onClick={() => setShowCompilerAfterSubmit(false)}
                            style={{ ...btnBase, background: "linear-gradient(135deg,#6366f1,#4f46e5)", color: "#fff", width: "auto", padding: "0 16px", fontSize: "0.9rem", fontWeight: 700 }}
                        >
                            ← Back to Results
                        </button>
                    )}
                    <Tooltip title="Run Query" arrow>
                        <span>
                            <button
                                onClick={handleRunCode}
                                disabled={loading || (isSubmitted && !showCompilerAfterSubmit)}
                                style={{ ...btnBase, background: loading ? "#6b7280" : "linear-gradient(135deg,#10b981,#059669)", color: "#fff", opacity: loading || (isSubmitted && !showCompilerAfterSubmit) ? 0.6 : 1 }}
                            >
                                {loading ? <Spinner size="sm" /> : <Play size={20} />}
                            </button>
                        </span>
                    </Tooltip>
                    <Tooltip title="Clear" arrow>
                        <span>
                            <button
                                onClick={handleClearCode}
                                disabled={loading || (isSubmitted && !showCompilerAfterSubmit)}
                                style={{ ...btnBase, background: "linear-gradient(135deg,#ef4444,#dc2626)", color: "#fff", opacity: loading || (isSubmitted && !showCompilerAfterSubmit) ? 0.6 : 1 }}
                            >
                                <Trash2 size={20} />
                            </button>
                        </span>
                    </Tooltip>
                    <Tooltip title="Toggle Theme" arrow>
                        <button
                            onClick={toggleTheme}
                            style={{ ...btnBase, background: isDarkMode ? "linear-gradient(135deg,#f59e0b,#d97706)" : "linear-gradient(135deg,#8b5cf6,#7c3aed)", color: "#fff" }}
                        >
                            {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
                        </button>
                    </Tooltip>
                    {/* Submit button — live session only */}
                    {!showCompilerAfterSubmit && (
                        <button
                            onClick={() => !isSubmitted && setShowSubmitConfirm(true)}
                            disabled={loading || isSubmitted}
                            style={{
                                ...btnBase,
                                width: "auto", padding: "0 18px", fontSize: "0.9rem", fontWeight: 700,
                                background: isSubmitted ? "linear-gradient(135deg,#6b7280,#4b5563)" : "linear-gradient(135deg,#10b981,#059669)",
                                color: "#fff", cursor: isSubmitted ? "not-allowed" : "pointer",
                                opacity: isSubmitted || loading ? 0.6 : 1,
                            }}
                        >
                            {isSubmitted ? "✓ Submitted" : "Submit"}
                        </button>
                    )}
                    {/* Timer */}
                    {timeLeft !== null && !showCompilerAfterSubmit && (
                        <div style={{
                            marginLeft: "auto",
                            background: timeLeft <= 300 ? (timeLeft <= 60 ? "rgba(239,68,68,0.15)" : "rgba(245,158,11,0.15)") : "rgba(16,185,129,0.12)",
                            border: `1px solid ${timeLeft <= 60 ? "#ef4444" : timeLeft <= 300 ? "#f59e0b" : "#10b981"}`,
                            borderRadius: "8px", padding: "4px 12px",
                            color: timeLeft <= 60 ? "#ef4444" : timeLeft <= 300 ? "#f59e0b" : "#10b981",
                            fontSize: "1rem", fontWeight: 700, fontVariantNumeric: "tabular-nums",
                        }}>
                            ⏱ {formatTime(timeLeft)}
                        </div>
                    )}
                    <div style={{ marginLeft: timeLeft !== null && !showCompilerAfterSubmit ? "8px" : "auto", color: textSecondary, fontSize: "0.85rem", fontWeight: 600, paddingRight: "8px" }}>
                        SQL
                    </div>
                </div>

                {/* Main row: left=problem, right=editor+output */}
                <div style={{ display: "flex", flex: 1, overflow: "hidden", minHeight: 0 }}>

                    {/* Left: problem statement */}
                    <div style={{
                        width: "35%", flexShrink: 0, overflowY: "auto",
                        padding: "1.25rem 1.5rem",
                        borderRight: isDarkMode ? "1px solid #4a5568" : "1px solid #e5e7eb",
                        background: isDarkMode ? "#1a202c" : "#ffffff",
                        boxSizing: "border-box",
                    }}>
                        <h3 style={{ color: isDarkMode ? "#93c5fd" : "#1976d2", fontWeight: 700, marginBottom: "0.75rem", fontSize: "1.05rem" }}>
                            Solve the following SQL problem.
                        </h3>
                        <p style={{ color: isDarkMode ? "#d1d5db" : "#374151", fontSize: "0.95rem", lineHeight: "1.75", margin: 0, whiteSpace: "pre-line" }}>
                            {problemStatement || "No problem statement provided."}
                        </p>
                    </div>

                    {/* Right: editor + output/tables */}
                    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0 }}>

                        {/* SQL editor */}
                        <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minHeight: 0 }}>
                            <div style={{
                                height: "30px", fontSize: "14px", fontWeight: 600,
                                padding: "8px 16px", display: "flex", alignItems: "center", justifyContent: "center",
                                background: isDarkMode ? "linear-gradient(90deg,#252544,#252544)" : "linear-gradient(90deg,#e0e7ff,#c3daff)",
                                borderBottom: isDarkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)",
                                color: isDarkMode ? "white" : "#333", flexShrink: 0,
                            }}>
                                SQL Editor
                            </div>
                            <CodeMirror
                                value={activeFile?.content || ""}
                                height="100%"
                                extensions={extensions}
                                onChange={onChange}
                                theme={isDarkMode ? oneDark : defaultLightThemeOption}
                                style={{ flex: 1, overflow: "hidden" }}
                            />
                        </div>

                        {/* Output + tables panel */}
                        <div style={{
                            height: "300px", flexShrink: 0,
                            borderTop: isDarkMode ? "2px solid #4a5568" : "2px solid #e5e7eb",
                            display: "flex", flexDirection: "column", overflow: "hidden",
                            background: isDarkMode ? "#1a202c" : "#ffffff",
                        }}>
                            <div style={{
                                padding: "8px 16px", fontWeight: 600, fontSize: "0.85rem",
                                color: isDarkMode ? "#93c5fd" : "#1e40af",
                                borderBottom: isDarkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid #e5e7eb",
                                flexShrink: 0,
                                background: isDarkMode ? "linear-gradient(90deg,#252544,#252544)" : "linear-gradient(90deg,#e0e7ff,#c3daff)",
                            }}>
                                Output
                            </div>
                            <div style={{ flex: 1, overflowY: "auto", padding: "0.75rem" }}>
                                {error ? (
                                    <pre style={{ color: "#ef4444", fontFamily: "Fira Code, monospace", fontSize: "0.9rem", margin: 0, whiteSpace: "pre-wrap" }}>{error}</pre>
                                ) : output ? (
                                    <pre style={{ color: isDarkMode ? "#e2e8f0" : "#1f2937", fontFamily: "Fira Code, monospace", fontSize: "0.9rem", margin: 0, whiteSpace: "pre-wrap" }}>{output}</pre>
                                ) : (
                                    <p style={{ color: isDarkMode ? "#9ca3af" : "#6b7280", fontStyle: "italic", fontSize: "0.9rem" }}>
                                        Run your query to see output here.
                                    </p>
                                )}
                                {/* Database tables accordion */}
                                {tables && (
                                    <div style={{ marginTop: "0.75rem" }}>
                                        <Accordion
                                            style={{
                                                backgroundColor: isDarkMode ? "#1a1a2e" : "#ffffff",
                                                color: isDarkMode ? "#e2e8f0" : "#374151",
                                                borderRadius: "10px",
                                                boxShadow: "none",
                                                border: `1px solid ${isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.08)"}`,
                                            }}
                                        >
                                            <AccordionSummary
                                                expandIcon={<ExpandMoreIcon style={{ color: isDarkMode ? "#e2e8f0" : "#374151" }} />}
                                                style={{ backgroundColor: isDarkMode ? "#2d3748" : "#f8fafc", borderRadius: "10px" }}
                                            >
                                                <Typography style={{ color: isDarkMode ? "#e2e8f0" : "#374151", fontWeight: "600", fontSize: "0.85rem" }}>
                                                    Database Tables
                                                </Typography>
                                            </AccordionSummary>
                                            <AccordionDetails style={{ padding: "10px" }}>
                                                {renderTables()}
                                            </AccordionDetails>
                                        </Accordion>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ProctoredCodeEditorDB;
